// components/AudioClipPlayer.jsx
// Plays a segment of an interview video as audio-only, given a source_locator string.
// Parses "Tape 2 00:12:34-00:12:51" → finds the MP4 file, seeks to start, plays until end.

import React from 'react';
import axios from 'axios';
import { AlertTriangle, Pause, Play, Volume2 } from 'lucide-react';
import { BACKEND_URL, VIDEO_SERVER_URL } from '../constants';
import { useToast } from '../toastContext';

// Nothing in the media pipeline below has a timeout of its own, so a stalled or
// unreachable media server would leave the button on amber 'loading' forever.
const LOAD_TIMEOUT_MS = 20000;

// Cache mp4 list and working dir across all instances
let _mp4Cache = null;
let _subdirCache = null;
let _cachePromise = null;

async function ensureCache() {
  if (_mp4Cache && _subdirCache !== null) return;
  if (_cachePromise) return _cachePromise;
  _cachePromise = (async () => {
    try {
      const [mp4Res, dirRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/transcribe/mp4s`),
        axios.get(`${BACKEND_URL}/working-dir`),
      ]);
      _mp4Cache = mp4Res.data || [];
      _subdirCache = dirRes.data?.working_subdir || '';
    } catch {
      _mp4Cache = [];
      _subdirCache = '';
    }
    _cachePromise = null;
  })();
  return _cachePromise;
}

/**
 * Parse a source_locator string into { part, startSec, endSec }.
 * Supports formats:
 *   "Tape 2 00:12:34-00:12:51"
 *   "Tape 2 00:12:34"
 *   "Part 3 01:05:00-01:06:30"
 *   "00:45:12"
 */
function parseSourceLocator(locator) {
  if (!locator) return null;

  // Extract part/tape number. Track whether the locator actually named one:
  // an assumed part must never be allowed to resolve to a different tape.
  const partMatch = locator.match(/(?:tape|part|pt)\s*(\d+)/i);
  const part = partMatch ? parseInt(partMatch[1], 10) : 1;
  const partExplicit = !!partMatch;

  // Extract time range
  const timePattern = /(\d{1,2}:\d{2}:\d{2}(?:\.\d+)?)/g;
  const times = [];
  let m;
  while ((m = timePattern.exec(locator)) !== null) {
    times.push(m[1]);
  }

  if (times.length === 0) return null;

  const toSeconds = (t) => {
    const parts = t.split(':').map(Number);
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
    if (parts.length === 2) return parts[0] * 60 + parts[1];
    return parts[0];
  };

  const startSec = toSeconds(times[0]);
  const endSec = times.length > 1 ? toSeconds(times[1]) : startSec + 30; // default 30s clip

  return { part, partExplicit, startSec, endSec };
}

// Resolve the mp4 for a locator. Previously this fell back to _mp4Cache[0]
// whenever the requested tape had no match, which silently played a DIFFERENT
// tape's audio at the cited timestamp — on a testimony archive that is a
// citation error, not a convenience. Now the only fallback kept is the
// unambiguous one: a single-tape interview whose locator never named a tape.
// Anything else returns null so the caller can say the tape is unavailable.
function buildVideoUrl(part, partExplicit) {
  if (!_mp4Cache || !_mp4Cache.length) return null;
  let mp4 = _mp4Cache.find(m => m.part === part);
  if (!mp4 && !partExplicit && _mp4Cache.length === 1) mp4 = _mp4Cache[0];
  if (!mp4) return null;
  const segments = [...(_subdirCache ? _subdirCache.split('/') : []), mp4.filename]
    .filter(Boolean)
    .map(s => encodeURIComponent(s));
  return `${VIDEO_SERVER_URL}/${segments.join('/')}`;
}


// ── Inline play button (small, next to source_locator text) ──
export function PlayButton({ sourceLocator, theme = {}, size = 'small', label = null }) {
  const [state, setState] = React.useState('idle'); // idle | loading | playing | paused | missing
  const [failNote, setFailNote] = React.useState(null); // why the last attempt failed
  const videoRef = React.useRef(null);
  const clipRef = React.useRef(null); // { startSec, endSec }
  const timerRef = React.useRef(null);
  const loadTimerRef = React.useRef(null);
  const { pushToast } = useToast();

  const clearLoadTimer = () => {
    if (loadTimerRef.current) { clearTimeout(loadTimerRef.current); loadTimerRef.current = null; }
  };

  const cleanup = () => {
    clearLoadTimer();
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (videoRef.current) { videoRef.current.pause(); videoRef.current.src = ''; videoRef.current = null; }
    setState('idle');
  };

  // Every failure used to reset to 'idle' with no explanation — the reviewer saw
  // a brief amber flash and a button back to blue. Land on the existing red
  // 'missing' chip instead and say why, both in the chip's tooltip/label and
  // once in a toast (the tooltip alone is unreachable on iPad).
  const fail = (note) => {
    clearLoadTimer();
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (videoRef.current) { videoRef.current.pause(); videoRef.current.src = ''; videoRef.current = null; }
    setFailNote(note);
    setState('missing');
    pushToast('audio-clip-play', 'error', `Audio clip could not play — ${note} (${sourceLocator})`);
  };

  // Unmount teardown only. `cleanup` is intentionally NOT a dependency: it is
  // recreated every render, so listing it would re-run this effect constantly
  // and its return value — the teardown — would stop playback mid-clip. The
  // function only touches refs, so the stale closure is harmless here.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => cleanup, []);

  const toggle = async (e) => {
    e.stopPropagation();

    // A second click while loading used to fall straight through to the "start
    // fresh" block below, building a SECOND <video> over videoRef.current — two
    // audio tracks at once plus a leaked, still-buffering element.
    if (state === 'loading') return;

    if (state === 'playing') {
      if (videoRef.current) videoRef.current.pause();
      setState('paused');
      return;
    }
    if (state === 'paused' && videoRef.current) {
      videoRef.current.play();
      setState('playing');
      return;
    }

    // Start fresh
    setFailNote(null);
    setState('loading');
    clearLoadTimer();
    loadTimerRef.current = setTimeout(() => {
      loadTimerRef.current = null;
      fail('the media file took too long to load');
    }, LOAD_TIMEOUT_MS);

    await ensureCache();

    const parsed = parseSourceLocator(sourceLocator);
    if (!parsed) { clearLoadTimer(); setState('idle'); return; }

    const url = buildVideoUrl(parsed.part, parsed.partExplicit);
    if (!url) { clearLoadTimer(); setState('missing'); return; }

    clipRef.current = parsed;

    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.preload = 'auto';
    video.src = url;
    videoRef.current = video;

    video.addEventListener('loadedmetadata', () => {
      video.currentTime = parsed.startSec;
    });

    video.addEventListener('seeked', () => {
      // If this element has already been abandoned (load timed out, cleanup ran)
      // it must not resurrect itself and start playing over the top.
      if (videoRef.current !== video) return;
      clearLoadTimer();
      video.play().then(() => {
        setState('playing');
        // Monitor playback — stop at endSec
        timerRef.current = setInterval(() => {
          if (video.currentTime >= parsed.endSec) {
            video.pause();
            clearInterval(timerRef.current);
            timerRef.current = null;
            setState('idle');
            videoRef.current = null;
          }
        }, 200);
      }).catch(() => fail('the browser blocked playback'));
    });

    video.addEventListener('error', () => {
      if (videoRef.current !== video) return;
      fail('the media file could not be loaded');
    });

    video.addEventListener('ended', () => {
      cleanup();
    });
  };

  if (!sourceLocator || !parseSourceLocator(sourceLocator)) return null;

  const isSmall = size === 'small';
  const iconSize = isSmall ? 10 : 14;
  const padding = isSmall ? '2px 5px' : '4px 8px';
  const fontSize = isSmall ? '0.5rem' : '0.62rem';

  const colors = {
    idle: { color: '#2563eb', bg: '#2563eb14', border: '#2563eb33' },
    loading: { color: '#f59e0b', bg: '#f59e0b14', border: '#f59e0b33' },
    playing: { color: '#16a34a', bg: '#16a34a14', border: '#16a34a33' },
    paused: { color: '#f59e0b', bg: '#f59e0b14', border: '#f59e0b33' },
    missing: { color: '#ef4444', bg: '#ef444414', border: '#ef444433' },
  };
  const c = colors[state] || colors.idle;

  const missingMsg = `Audio unavailable — ${failNote || `no media file matches "${sourceLocator}"`}`;

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={state === 'loading'}
      aria-label={state === 'playing' ? 'Pause audio clip' : state === 'missing' ? missingMsg : `Play audio clip: ${sourceLocator}`}
      aria-pressed={state === 'playing'}
      title={state === 'playing' ? 'Pause audio clip' : state === 'missing' ? missingMsg : `Play: ${sourceLocator}`}
      style={{
        padding, fontSize, fontWeight: 700, borderRadius: isSmall ? '6px' : '8px',
        cursor: state === 'loading' ? 'default' : 'pointer',
        display: 'inline-flex', alignItems: 'center', gap: '3px',
        color: c.color, background: c.bg, border: `1px solid ${c.border}`,
        transition: 'all 0.15s',
      }}
    >
      {state === 'loading' ? (
        <Volume2 size={iconSize} strokeWidth={2} style={{ animation: 'pulse-amber 1s infinite' }} />
      ) : state === 'playing' ? (
        <Pause size={iconSize} strokeWidth={2} />
      ) : state === 'missing' ? (
        <AlertTriangle size={iconSize} strokeWidth={2} />
      ) : (
        <Play size={iconSize} strokeWidth={2} />
      )}
      {state === 'missing'
        ? 'Unavailable'
        : (label || !isSmall) && (state === 'playing' ? 'Playing' : state === 'paused' ? 'Paused' : (label || 'Play'))}
    </button>
  );
}


// ── Floating mini-player bar (shown when audio is playing globally) ──
// This is an optional enhancement for later — the PlayButton works standalone.
export default PlayButton;
